/* eslint-disable */
/**
 * @fileoverview Upcoming Tasks Component
 *
 * Displays bookmarked clients section (which serves as quick access tasks).
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { FiBookmark, FiRefreshCw, FiFileText } from 'react-icons/fi';

/**
 * UpcomingTasks Component (Bookmarked Clients)
 *
 * @param {Object} props
 * @param {Array} props.bookmarkedClients - List of bookmarked clients
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Refresh callback
 * @param {Function} props.onClientClick - Client click callback
 * @param {Function} props.trackCustomEvent - Event tracking callback
 * @param {boolean} props.canViewClients - Permission to view clients
 * @returns {JSX.Element|null}
 */
const UpcomingTasks = memo(({
    bookmarkedClients,
    loading,
    onRefresh,
    onClientClick,
    trackCustomEvent,
    canViewClients
}) => {
    // Don't render if user can't view clients
    if (!canViewClients) {
        return null;
    }

    const handleClientClick = (client) => {
        // Track bookmarked client click
        if (trackCustomEvent) {
            trackCustomEvent('bookmarked_client_clicked', {
                client_id: client.id,
                client_name: client.legal_name,
                source: 'dashboard_bookmarks'
            });
        }
        onClientClick(client);
    };

    // Render skeleton cards during loading
    const renderSkeletonCards = () => {
        return Array.from({ length: 4 }).map((_, index) => (
            <li key={`skeleton-${index}`} className="bookmark-card db-skeleton-card" aria-hidden="true">
                <div className="bookmark-avatar db-skeleton-avatar" />
                <div className="bookmark-details">
                    <span className="db-skeleton-cell db-skeleton-cell--name" />
                    <span className="db-skeleton-cell db-skeleton-cell--id" />
                </div>
            </li>
        ));
    };

    return (
        <section className="dashboard-section">
            <div className="section-header">
                <h2 className="section-title">
                    <FiBookmark className="section-icon" /> Bookmarked Clients
                </h2>
                <button
                    className="d-refresh-button"
                    onClick={onRefresh}
                    disabled={loading}
                >
                    {loading ? (
                        <FiRefreshCw className="spinner" />
                    ) : (
                        <FiRefreshCw />
                    )}
                </button>
            </div>
            <div className="bookmarks-list">
                {loading && (!bookmarkedClients || bookmarkedClients.length === 0) ? (
                    <ul className="bookmarks-grid">
                        {renderSkeletonCards()}
                    </ul>
                ) : bookmarkedClients?.length > 0 ? (
                    <ul className="bookmarks-grid">
                        {bookmarkedClients.slice(0, 4).map(client => (
                            <li
                                key={client.id}
                                className="bookmark-card cursor-pointer"
                                data-client-id={client.id}
                                onClick={() => handleClientClick(client)}
                            >
                                <div className="bookmark-avatar">
                                    {client.legal_name.charAt(0).toUpperCase()}
                                </div>
                                <div className="bookmark-details">
                                    <h3>{client.legal_name}</h3>
                                    <p>Client ID: {client.client_id}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="empty-state">
                        <FiFileText className="empty-icon" />
                        <p>No bookmarked clients yet</p>
                        <span>Bookmark clients to see them here</span>
                    </div>
                )}
            </div>
        </section>
    );
});

UpcomingTasks.displayName = 'UpcomingTasks';

export default UpcomingTasks;
